import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// BPO RH — Custo por Colaborador: quanto cada pessoa custa para a empresa no mês
// (bruto + benefícios líquidos + FGTS + INSS patronal + provisões com encargos),
// com exportação em Excel e envio ao cliente (sempre com prévia; o Enviar fica
// dentro dela). Backend: core/views_custo_colaborador.py
const API = process.env.REACT_APP_API_BASE;
const URL_CUSTO = `${API}/api/bporh/custo-colaborador/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
// competência padrão = mês anterior; data local e setDate(1) evitam mês errado
// perto da meia-noite do dia 1 e o estouro de 31/03 − 1 mês
const mesAtual = () => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const erroMsg = (e, padrao) => e.response?.data?.mensagem || e.response?.data?.erro || e.response?.data?.error || padrao;
const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
// '2,5' / '2.5' → 2.5 (vazio ou inválido → 0, só para a prévia do percentual)
const num = (x) => { const n = parseFloat(String(x ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
// mesma conta do backend (pct_patronal): Simples 0; anexo IV 20 + RAT×FAP;
// Presumido/Real 20 + RAT×FAP + terceiros
const pctPatronal = ({ regime, rat, fap, terceiros }) => {
  if (regime === 'simples') return 0;
  const base = 20 + num(rat) * num(fap);
  return regime === 'presumido_real' ? base + num(terceiros) : base;
};
// o backend também manda este aviso; com o alerta próprio da tela ele sairia duplicado
const AVISO_PADRAO = 'Encargos da empresa não configurados';

// colunas de valor da tabela, na ordem da planilha (grupo do cabeçalho de 2 linhas)
const COLUNAS = [
  ['salario', 'Salário bruto', 'sal'], ['comissoes', 'Comissões', 'sal'], ['dependentes_ir', 'Dep. IR', 'sal', 'int'],
  ['inss', 'INSS', 'desc'], ['irrf', 'IRRF', 'desc'], ['liquido', 'Líquido', 'desc'],
  ['vt', 'VT', 'custo'], ['va', 'VA', 'custo'], ['ho', 'Home office', 'custo'], ['saude', 'Saúde', 'custo'],
  ['odonto', 'Odonto', 'custo'], ['fgts', 'FGTS', 'custo'], ['patronal', 'INSS patronal', 'custo'],
  ['prov_13', 'Prov. 13º', 'custo'], ['prov_ferias', 'Prov. férias', 'custo'], ['prov_terco', 'Prov. 1/3', 'custo'],
  ['encargos_prov', 'Encargos s/ prov.', 'custo'], ['total', 'Total custo', 'custo'],
];
const GRUPOS = [['sal', 'Salário'], ['desc', 'Descontos (informativo)'], ['custo', 'Custo empresa']];
// coluna Nome fixa à esquerda no scroll horizontal
const FIXA = { position: 'sticky', left: 0, background: 'var(--po-surface)', zIndex: 2, minWidth: 220 };

const BpoRhCustoColaborador = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteId = searchParams.get('cliente') || '';
  const mes = searchParams.get('mes') || mesAtual();
  const filialId = searchParams.get('filial') || '';
  const [clientes, setClientes] = useState(null);
  const [admLista, setAdmLista] = useState(false);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState('');      // qual ação está em andamento
  const [busca, setBusca] = useState('');
  const [depto, setDepto] = useState('');
  const [foraAberto, setForaAberto] = useState(false);
  const [detalhe, setDetalhe] = useState(null);    // linha do "Como foi calculado"
  const [cfg, setCfg] = useState(null);            // modal Encargos da empresa
  const [envio, setEnvio] = useState(null);        // {carregando, previa, form, enviando, erro}
  // contra corrida: trocou empresa/mês/filial com requisição no ar → a resposta
  // antiga não sobrescreve a tela nova
  const reqRef = useRef(0);
  const atualRef = useRef({ clienteId, mes, filialId });
  atualRef.current = { clienteId, mes, filialId };
  const aindaAtual = (c, m, f) => atualRef.current.clienteId === c && atualRef.current.mes === m && atualRef.current.filialId === f;

  const trocar = (patch) => {
    const p = { cliente: clienteId, mes, filial: filialId, ...patch };
    if ('cliente' in patch && patch.cliente !== clienteId) p.filial = ''; // filial é da empresa anterior
    setSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v)));
  };

  // lista de empresas: com uma só, já abre direto
  useEffect(() => {
    axios.get(URL_CUSTO, { params: { _: Date.now() }, headers: headers() })
      .then((r) => {
        const cls = r.data.clientes || [];
        setClientes(cls); setAdmLista(!!r.data.eh_adm);
        if (cls.length === 1 && !searchParams.get('cliente')) {
          setSearchParams({ cliente: String(cls[0].id), mes: searchParams.get('mes') || mesAtual() });
        }
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível carregar as empresas')}`));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // empresa/mês/filial da tela no momento da chamada
  const base = () => ({ cliente_id: Number(clienteId), mes, ...(filialId ? { filial_id: Number(filialId) } : {}) });

  const carregar = useCallback(() => {
    const id = ++reqRef.current;
    if (!clienteId) { setDados(null); setCarregando(false); return; }
    setCarregando(true);
    axios.get(URL_CUSTO, { params: { cliente_id: clienteId, mes, ...(filialId ? { filial_id: filialId } : {}), _: Date.now() }, headers: headers() })
      .then((r) => { if (id === reqRef.current) setDados(r.data); })
      .catch((e) => { if (id !== reqRef.current) return; setDados(null); notificar(`❌ ${erroMsg(e, 'Não foi possível calcular o custo')}`); })
      .finally(() => { if (id === reqRef.current) setCarregando(false); });
  }, [clienteId, mes, filialId]);
  useEffect(() => { setDepto(''); setDetalhe(null); carregar(); }, [carregar]);

  const exportar = () => {
    const m0 = mes;
    setOcupado('exportar');
    axios.get(URL_CUSTO, { params: { ...base(), exportar: 1, _: Date.now() }, headers: headers(), responseType: 'blob' })
      .then((r) => {
        const cd = r.headers['content-disposition'] || '';
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        const nome = m ? decodeURIComponent(m[1]) : `Custo_por_colaborador_${m0}.xlsx`;
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
        // revogar no mesmo tick pode cancelar o download em alguns navegadores
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
      })
      .catch(async (e) => {
        // com responseType blob o erro também chega como blob
        let msg = 'Não foi possível exportar a planilha';
        try { msg = JSON.parse(await e.response.data.text()).mensagem || msg; } catch (_) { /* mantém a mensagem padrão */ }
        notificar(`❌ ${msg}`);
      })
      .finally(() => setOcupado(''));
  };

  // ── Encargos da empresa ──
  const abrirCfg = () => {
    const c = dados?.config || {};
    const txt = (v) => (v == null ? '' : String(v).replace('.', ','));
    setCfg({ regime: c.regime || 'presumido_real', rat: txt(c.rat), fap: txt(c.fap), terceiros: txt(c.terceiros) });
  };
  const salvarCfg = () => {
    const c0 = clienteId; const m0 = mes; const f0 = filialId;
    setOcupado('config');
    axios.post(URL_CUSTO, { acao: 'config', ...base(), regime: cfg.regime, rat: cfg.rat, fap: cfg.fap, terceiros: cfg.terceiros }, { headers: headers() })
      .then((r) => {
        notificar('✅ Encargos da empresa salvos. Custo recalculado.'); setCfg(null);
        if (aindaAtual(c0, m0, f0)) setDados(r.data);
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível salvar os encargos')}`))
      .finally(() => setOcupado(''));
  };

  // ── Prévia e envio do e-mail ao cliente ──
  const previa = (form) => {
    const c0 = clienteId; const m0 = mes; const f0 = filialId;
    setEnvio((s) => ({ ...(s || {}), carregando: true, form: form || s?.form, erro: null }));
    const corpo = { acao: 'email', ...base(), enviar: false };
    if (form) {
      if (form.operador_id) corpo.operador_id = Number(form.operador_id);
      corpo.destinatarios = form.destinatarios || ''; corpo.copia = form.copia || '';
    }
    axios.post(URL_CUSTO, corpo, { headers: headers() })
      .then((r) => {
        if (!aindaAtual(c0, m0, f0)) return;
        const p = r.data.previa;
        setEnvio({ carregando: false, enviando: false, previa: p,
          form: form || { operador_id: p.operador_id || '', destinatarios: p.destinatarios || '', copia: p.copia || '' } });
      })
      .catch((e) => { setEnvio(null); notificar(`❌ ${erroMsg(e, 'Não foi possível montar a prévia')}`); });
  };
  const enviar = () => {
    const c0 = clienteId; const m0 = mes; const f0 = filialId;
    const { previa: p, form } = envio;
    setEnvio((s) => ({ ...s, enviando: true, erro: null }));
    axios.post(URL_CUSTO, {
      acao: 'email', ...base(), enviar: true, operador_id: Number(form.operador_id),
      destinatarios: form.destinatarios, copia: form.copia, assunto: p.assunto, conteudo_html: p.conteudo_html,
    }, { headers: headers() })
      .then((r) => { setEnvio(null); notificar(`✅ ${r.data.mensagem}`); if (aindaAtual(c0, m0, f0)) carregar(); })
      .catch((e) => setEnvio((s) => (s ? { ...s, enviando: false, erro: erroMsg(e, 'Não foi possível enviar') } : s)));
  };

  const ehAdm = dados ? !!dados.eh_adm : admLista;
  const config = dados?.config || {};
  const regimes = dados?.regimes || {};
  const linhas = useMemo(() => dados?.linhas || [], [dados]);
  const departamentos = useMemo(() => [...new Set(linhas.map((l) => l.departamento).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'pt-BR')), [linhas]);
  const visiveis = useMemo(() => {
    const q = semAcento(busca.trim());
    return linhas.filter((l) => (!q || semAcento(l.nome).includes(q)) && (!depto || l.departamento === depto));
  }, [linhas, busca, depto]);
  const soma = (k) => visiveis.reduce((s, l) => s + Number(l[k] || 0), 0);
  const totais = dados?.totais || {};
  const avisos = (dados?.avisos || []).filter((a) => config.configurado !== false || !String(a).startsWith(AVISO_PADRAO));
  const ultimo = (dados?.envios || [])[0];
  const fora = dados?.fora || [];
  const filiais = dados?.filiais || [];
  const semLinhas = !linhas.length;

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-calculator header-icon me-2" aria-hidden="true"></i>
          <div><h2 className="titulo-pagina m-0">BPO RH — Custo por Colaborador</h2>
            <div className="po-sub">Quanto cada pessoa custa para a empresa no mês: salário, benefícios, encargos e provisões.</div></div></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 230 }} value={clienteId} onChange={(e) => trocar({ cliente: e.target.value })} aria-label="Empresa">
            <option value="">{clientes ? 'Escolha a empresa…' : 'Carregando…'}</option>
            {(clientes || []).map((c) => <option key={c.id} value={c.id}>{c.cliente}</option>)}
          </select>
          <input type="month" className="form-control form-control-sm" style={{ width: 150 }} value={mes} onChange={(e) => e.target.value && trocar({ mes: e.target.value })} aria-label="Competência" />
          {filiais.length > 0 && (
            <select className="form-select form-select-sm" style={{ width: 180 }} value={filialId} onChange={(e) => trocar({ filial: e.target.value })} aria-label="Filial">
              <option value="">Todas as filiais</option>
              {filiais.map((f) => <option key={f.id} value={String(f.id)}>{f.nome}</option>)}
            </select>)}
          <button type="button" className="gr-btn" onClick={carregar} disabled={carregando || !clienteId}><i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar</button>
          {clienteId && (<>
            <button type="button" className="gr-btn" onClick={abrirCfg} disabled={!dados} title={ehAdm ? 'Regime, RAT, FAP e terceiros da empresa' : 'Consultar os encargos usados no cálculo'}>
              <i className="fas fa-building-columns" aria-hidden="true"></i>Encargos da empresa</button>
            <button type="button" className="gr-btn" onClick={exportar} disabled={!dados || semLinhas || !!ocupado}>
              <i className={`fas ${ocupado === 'exportar' ? 'fa-spinner fa-spin' : 'fa-file-excel'}`} aria-hidden="true"></i>Exportar Excel</button>
            <button type="button" className="gr-btn primaria" onClick={() => previa()} disabled={!dados || semLinhas || !!ocupado || !!envio}>
              <i className="fas fa-envelope-open-text" aria-hidden="true"></i>Enviar ao cliente</button>
          </>)}
        </div>
      </div>

      {!clienteId && clientes && (
        <div className="text-muted small py-4 text-center">{clientes.length ? 'Escolha a empresa para começar.' : 'Nenhuma empresa disponível.'}</div>)}

      {dados && (<>
        {config.configurado === false && (
          <div className="alert alert-warning small py-2"><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>
            Usando o padrão Lucro Presumido/Real (27,8%). Confirme o regime da empresa em <b>Encargos da empresa</b>.</div>)}
        {avisos.length > 0 && (
          <div className="alert alert-warning small py-2">{avisos.map((a, i) => <div key={`${i}-${a}`}><i className="fas fa-triangle-exclamation me-1" aria-hidden="true"></i>{a}</div>)}</div>)}

        <div className="d-flex flex-wrap gap-3 align-items-center mb-3 small">
          <span className="po-sub"><i className="fas fa-building-columns me-1" aria-hidden="true"></i>{config.regime_rotulo || '—'} · INSS patronal {pct(config.pct_patronal)}</span>
          {ultimo && (
            <span className="po-sub"><i className="fas fa-paper-plane me-1" aria-hidden="true"></i>
              Último envio: {ultimo.enviado_em}{ultimo.enviado_por ? ` por ${ultimo.enviado_por}` : ''} para {ultimo.destinatarios || ultimo.para}</span>)}
        </div>

        <div className="row g-3 mb-3">
          {[
            ['Pessoas', totais.pessoas ?? 0, 'fa-users'],
            ['Custo total', brl(totais.total), 'fa-sack-dollar'],
            ['Custo médio', brl(totais.medio), 'fa-user-tag'],
            ['Encargos', brl(totais.encargos), 'fa-landmark'],
            ['Benefícios', brl(totais.beneficios), 'fa-gift'],
            ['Provisões', brl(totais.provisoes), 'fa-piggy-bank'],
          ].map(([rot, val, ic]) => (
            <div className="col-6 col-lg" key={rot}>
              <section className="po-card po-enter h-100"><div className="po-card-body">
                <div className="po-sub"><i className={`fas ${ic} me-1`} aria-hidden="true"></i>{rot}</div>
                <div className="fs-5 fw-bold tabular">{val}</div>
              </div></section></div>))}
        </div>

        <section className="po-card po-enter mb-3"><header className="po-card-head">
          <h3><i className="fas fa-table" aria-hidden="true"></i>Colaboradores · {dados.mes_rotulo || dados.mes}</h3>
          <div className="d-flex gap-2 flex-wrap">
            <input type="search" className="form-control form-control-sm" style={{ width: 200 }} placeholder="Buscar por nome…" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar por nome" />
            <select className="form-select form-select-sm" style={{ width: 200 }} value={depto} onChange={(e) => setDepto(e.target.value)} aria-label="Departamento" disabled={!departamentos.length}>
              <option value="">Todos os departamentos</option>
              {departamentos.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div></header>
          <div className="po-card-body"><div className="po-scroll"><table className="table table-hover po-table align-middle mb-0" style={{ fontSize: '.82rem' }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...FIXA, zIndex: 3 }}>Nome</th>
                <th rowSpan={2}>Vínculo</th>
                {GRUPOS.map(([g, rot]) => <th key={g} colSpan={COLUNAS.filter((c) => c[2] === g).length} className="text-center">{rot}</th>)}
              </tr>
              {/* o th do po-table já é sticky no topo: a 2ª linha encosta embaixo da 1ª */}
              <tr>{COLUNAS.map(([k, rot]) => <th key={k} className="text-end text-nowrap" style={{ top: 'var(--po-row-h)' }}>{rot}</th>)}</tr>
            </thead>
            <tbody>{visiveis.map((l, i) => (
              <tr key={`${l.convenia_id || l.nome}-${i}`} style={{ cursor: 'pointer' }} onClick={() => setDetalhe(l)} title="Ver como foi calculado">
                <td style={FIXA}><div className="po-nome">{l.nome}</div>
                  <div className="po-sub small">{[l.cargo, l.departamento].filter(Boolean).join(' · ') || '—'}</div>
                  {l.aviso && <div className="small" style={{ color: 'var(--po-bad)' }}><i className="fas fa-triangle-exclamation me-1" aria-hidden="true"></i>{l.aviso}</div>}</td>
                <td className="text-nowrap">{l.vinculo || '—'}</td>
                {COLUNAS.map(([k, , , fmt]) => (
                  <td key={k} className={`text-end tabular text-nowrap${k === 'total' ? ' fw-bold' : ''}`}>{fmt === 'int' ? (l[k] || 0) : brl(l[k] || 0)}</td>))}
              </tr>))}
              {!visiveis.length && <tr><td colSpan={COLUNAS.length + 2} className="text-muted small text-center py-3">
                {semLinhas ? `Nenhum colaborador com custo de folha em ${dados.mes_rotulo || dados.mes}.` : 'Nenhum colaborador com esse filtro.'}</td></tr>}
            </tbody>
            {visiveis.length > 0 && (
              <tfoot><tr className="fw-bold">
                <td style={FIXA}>Total ({visiveis.length})</td><td></td>
                {COLUNAS.map(([k, , , fmt]) => <td key={k} className="text-end tabular text-nowrap">{fmt === 'int' ? soma(k) : brl(soma(k))}</td>)}
              </tr></tfoot>)}
          </table></div>
            <div className="po-sub small mt-2">Clique em uma pessoa para ver como o custo foi calculado. INSS, IRRF e líquido são do colaborador: aparecem só como informação e não entram no custo.</div>
          </div></section>

        {fora.length > 0 && (
          <section className="po-card po-enter mb-3"><header className="po-card-head">
            <h3><button type="button" className="btn btn-link p-0 text-decoration-none" style={{ color: 'inherit', font: 'inherit' }} onClick={() => setForaAberto((v) => !v)} aria-expanded={foraAberto}>
              <i className={`fas ${foraAberto ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} aria-hidden="true"></i>Fora do cálculo ({fora.length})</button></h3></header>
            {foraAberto && (
              <div className="po-card-body">
                <div className="po-sub small mb-2">PJ, sócios (pró-labore) e autônomos não têm encargos de folha, por isso ficam fora do custo por colaborador.</div>
                <ul className="small mb-0">{fora.map((f, i) => <li key={`${f.nome}-${i}`}>{f.nome} <span className="po-sub">— {f.vinculo || 'sem vínculo'}</span></li>)}</ul>
              </div>)}
          </section>)}
      </>)}

      {/* ─── Modal: como foi calculado ─── */}
      {detalhe && (
        <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,.5)' }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"><div className="modal-content">
            <div className="modal-header" style={{ background: '#fff' }}>
              <h5 className="modal-title" style={{ color: '#212529' }}><i className="fas fa-calculator me-2" aria-hidden="true"></i>Como foi calculado — {detalhe.nome}
                <div className="small fw-normal" style={{ color: '#6c757d' }}>CPF {detalhe.cpf || '—'} · {detalhe.vinculo || '—'}{detalhe.cargo ? ` · ${detalhe.cargo}` : ''}</div></h5>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setDetalhe(null)}></button></div>
            <div className="modal-body">
              {detalhe.aviso && <div className="alert alert-warning small py-2"><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>{detalhe.aviso}</div>}
              <ol className="small mb-3">{(detalhe.passos || []).map((p, i) => <li key={i} className="mb-1">{p}</li>)}</ol>
              <h6 className="fw-bold mb-2">Benefícios: pago × descontado</h6>
              <table className="table table-sm small align-middle mb-3">
                <thead><tr><th>Benefício</th><th className="text-end">Pago pela empresa</th><th className="text-end">Descontado do colaborador</th><th className="text-end">Custo</th></tr></thead>
                <tbody>
                  <tr><td>VA</td><td className="text-end tabular">{brl(detalhe.va_pago || 0)}</td><td className="text-end tabular">{brl(detalhe.va_desconto || 0)}</td><td className="text-end tabular fw-bold">{brl(detalhe.va || 0)}</td></tr>
                  <tr><td>VT</td><td className="text-end tabular">{brl(detalhe.vt_pago || 0)}</td><td className="text-end tabular">{brl(detalhe.vt_desconto || 0)}</td><td className="text-end tabular fw-bold">{brl(detalhe.vt || 0)}</td></tr>
                  <tr><td>Home office</td><td className="text-end tabular">{brl(detalhe.ho_pago || 0)}</td><td className="text-end tabular">—</td><td className="text-end tabular fw-bold">{brl(detalhe.ho || 0)}</td></tr>
                  <tr><td>Saúde (parte da empresa)</td><td className="text-end tabular">{brl(detalhe.saude_empresa || 0)}</td><td className="text-end tabular">—</td><td className="text-end tabular fw-bold">{brl(detalhe.saude || 0)}</td></tr>
                  <tr><td>Odonto (parte da empresa)</td><td className="text-end tabular">{brl(detalhe.odonto_empresa || 0)}</td><td className="text-end tabular">—</td><td className="text-end tabular fw-bold">{brl(detalhe.odonto || 0)}</td></tr>
                </tbody>
              </table>
              <div className="d-flex flex-wrap gap-3 small">
                <span>FGTS {pct(detalhe.pct_fgts)}</span><span>INSS patronal {pct(detalhe.pct_patronal)}</span>
                <span>Encargos {brl(detalhe.encargos)}</span><span>Provisões {brl(detalhe.provisoes)}</span>
                <span className="fw-bold">Total {brl(detalhe.total)}</span>
              </div>
            </div>
            <div className="modal-footer bg-light">
              <button type="button" className="btn btn-secondary" onClick={() => setDetalhe(null)}>Fechar</button>
            </div>
          </div></div></div>)}

      {/* ─── Modal: encargos da empresa (só adm edita) ─── */}
      {cfg && (() => {
        const semTerceiros = cfg.regime !== 'presumido_real';
        const salvando = ocupado === 'config';
        return (
          <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,.5)' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered"><div className="modal-content">
              <div className="modal-header" style={{ background: '#fff' }}>
                <h5 className="modal-title" style={{ color: '#212529' }}><i className="fas fa-building-columns me-2" aria-hidden="true"></i>Encargos da empresa
                  <div className="small fw-normal" style={{ color: '#6c757d' }}>{dados?.cliente}</div></h5>
                <button type="button" className="btn-close" aria-label="Fechar" disabled={salvando} onClick={() => setCfg(null)}></button></div>
              <div className="modal-body">
                {!ehAdm && <div className="alert alert-light border small py-2"><i className="fas fa-lock me-2" aria-hidden="true"></i>Somente consulta: quem altera os encargos é o administrador.</div>}
                <label className="form-label small fw-bold mb-1">Regime tributário</label>
                <select className="form-select form-select-sm mb-3" value={cfg.regime} disabled={!ehAdm || salvando} onChange={(e) => setCfg({ ...cfg, regime: e.target.value })}>
                  {Object.entries(regimes).map(([k, rot]) => <option key={k} value={k}>{rot}</option>)}
                </select>
                <div className="row g-2">
                  <div className="col-4"><label className="form-label small fw-bold mb-1">RAT % (0 a 3)</label>
                    <input type="text" inputMode="decimal" className="form-control form-control-sm" value={cfg.rat} disabled={!ehAdm || salvando || cfg.regime === 'simples'} onChange={(e) => setCfg({ ...cfg, rat: e.target.value })} /></div>
                  <div className="col-4"><label className="form-label small fw-bold mb-1">FAP (0,5 a 2)</label>
                    <input type="text" inputMode="decimal" className="form-control form-control-sm" value={cfg.fap} disabled={!ehAdm || salvando || cfg.regime === 'simples'} onChange={(e) => setCfg({ ...cfg, fap: e.target.value })} /></div>
                  <div className="col-4"><label className="form-label small fw-bold mb-1">Terceiros % (0 a 10)</label>
                    <input type="text" inputMode="decimal" className="form-control form-control-sm" value={cfg.terceiros} disabled={!ehAdm || salvando || semTerceiros}
                      title={semTerceiros ? 'Não se aplica a este regime' : undefined} onChange={(e) => setCfg({ ...cfg, terceiros: e.target.value })} /></div>
                </div>
                <div className="form-text mb-3">Fonte: GFIP/eSocial da empresa (RAT e FAP).{semTerceiros ? ' Terceiros não se aplica a este regime.' : ''}</div>
                <div className="border rounded p-2 small">INSS patronal resultante: <b className="tabular">{pct(pctPatronal(cfg))}</b>
                  <div className="po-sub">{cfg.regime === 'simples' ? 'Simples Nacional (exceto anexo IV): sem INSS patronal.'
                    : cfg.regime === 'simples_iv' ? '20% + RAT × FAP.' : '20% + RAT × FAP + terceiros.'}</div></div>
                {config.atualizado_por && <div className="po-sub small mt-2">Atualizado por {config.atualizado_por}{config.atualizado_em ? ` em ${config.atualizado_em}` : ''}.</div>}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" disabled={salvando} onClick={() => setCfg(null)}>{ehAdm ? 'Cancelar' : 'Fechar'}</button>
                {ehAdm && (
                  <button type="button" className="btn btn-primary" disabled={salvando} onClick={salvarCfg}>
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`} aria-hidden="true"></i>Salvar</button>)}
              </div>
            </div></div></div>);
      })()}

      {/* ─── Modal: prévia e envio ao cliente (o Enviar fica aqui dentro) ─── */}
      {envio && (envio.previa || envio.carregando) && (() => {
        const p = envio.previa || {};
        const form = envio.form || {};
        const setForm = (patch) => setEnvio((s) => ({ ...s, form: { ...s.form, ...patch } }));
        const pronto = !envio.carregando && p.assunto && form.operador_id && (form.destinatarios || '').trim();
        const r = p.resumo || {};
        const t = r.totais || {};
        return (
          <div className="modal" tabIndex="-1" role="dialog" aria-modal="true" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 980 }}>
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-envelope-open-text me-2" aria-hidden="true"></i>Custo por colaborador de {dados?.mes_rotulo} — {dados?.cliente}</h5>
                    <small style={{ color: '#6c757d' }}>e-mail para o cliente com o resumo do custo e o Excel por colaborador</small>
                  </div>
                  <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setEnvio(null)} disabled={envio.enviando}></button>
                </div>
                <div className="modal-body">
                  {envio.carregando && !p.assunto ? (
                    <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-2x mb-2" style={{ color: 'var(--po-cyan)' }} aria-hidden="true"></i><div>Montando o resumo e o Excel…</div></div>
                  ) : (
                    <>
                      {envio.erro && <div className="alert alert-danger py-2 small"><i className="fas fa-circle-exclamation me-2" aria-hidden="true"></i>{envio.erro}</div>}
                      {r.config_padrao && (
                        <div className="alert alert-warning small py-2"><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>Os encargos da empresa não foram confirmados: o cálculo usa o padrão Lucro Presumido/Real. Confira em Encargos da empresa antes de enviar.</div>)}
                      <div className="row g-2 align-items-end mb-2">
                        <div className="col-md-3">
                          <label className="form-label small fw-bold mb-1 text-muted">Quem envia</label>
                          <select className="form-select form-select-sm" value={form.operador_id || ''} onChange={(e) => setForm({ operador_id: e.target.value })}>
                            <option value="">— operador —</option>
                            {(p.operadores || []).map((o) => <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.pronto ? '' : ' (sem senha de app)'}</option>)}
                          </select>
                        </div>
                        <div className="col-md-9">
                          <label className="form-label small fw-bold mb-1 text-muted">Para (e-mail do cliente)</label>
                          <input type="text" className="form-control form-control-sm" placeholder="financeiro@cliente.com; rh@cliente.com" value={form.destinatarios || ''} onChange={(e) => setForm({ destinatarios: e.target.value })} />
                        </div>
                        <div className="col-md-11">
                          <label className="form-label small fw-bold mb-1 text-muted">Cópia (opcional)</label>
                          <input type="text" className="form-control form-control-sm" placeholder="contabilidade@cliente.com" value={form.copia || ''} onChange={(e) => setForm({ copia: e.target.value })} />
                        </div>
                        <div className="col-md-1 d-flex">
                          <button type="button" className="btn btn-sm btn-outline-primary flex-grow-1" onClick={() => previa(form)} disabled={envio.carregando} title="Refazer a prévia com estes dados" aria-label="Refazer a prévia">
                            <i className={`fas ${envio.carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>

                      <div className="row g-2 mb-2 small">
                        {[
                          ['Pessoas', t.pessoas ?? 0], ['Custo total', brl(t.total)], ['Custo médio', brl(t.medio)],
                          ['Encargos', brl(t.encargos)], ['Benefícios', brl(t.beneficios)], ['Provisões', brl(t.provisoes)],
                        ].map(([rot, val]) => (
                          <div className="col-6 col-md-2" key={rot}><div className="border rounded p-2 h-100"><div className="fw-bold text-muted">{rot}</div><div className="tabular">{val}</div></div></div>))}
                      </div>
                      <div className="small text-muted mb-2">{r.regime || '—'} · INSS patronal {pct(r.pct_patronal)}{r.fora ? ` · ${r.fora} pessoa(s) fora do cálculo` : ''}</div>
                      {(r.departamentos || []).length > 0 && (
                        <table className="table table-sm small align-middle mb-2">
                          <thead><tr><th>Departamento</th><th className="text-end">Pessoas</th><th className="text-end">Custo total</th></tr></thead>
                          <tbody>{r.departamentos.map((d, i) => (
                            <tr key={`${d.departamento}-${i}`}><td>{d.departamento || 'Sem departamento'}</td><td className="text-end tabular">{d.pessoas}</td><td className="text-end tabular">{brl(d.total)}</td></tr>))}</tbody>
                        </table>)}

                      <div className="small mb-2">
                        <div><b>Assunto:</b> {p.assunto}</div>
                        <div><b>De:</b> {p.remetente || '—'}</div>
                        <div><b>Para:</b> {form.destinatarios || <span className="text-danger">informe o e-mail do cliente</span>}</div>
                        {form.copia && <div><b>Cópia:</b> {form.copia}</div>}
                        <div><b>Anexo:</b> {(p.anexos || []).map((a) => `${a.nome} (${kb(a.tamanho)}) — ${a.descricao}`).join(', ')}</div>
                      </div>
                      <div className="border rounded" style={{ maxHeight: 380, overflow: 'auto' }}>
                        {/* sandbox="" obrigatório: srcdoc herda a origem do app */}
                        <iframe title="Prévia do custo por colaborador" srcDoc={p.corpo_previa || p.corpo_html} sandbox="" style={{ width: '100%', height: 380, border: 0 }} />
                      </div>
                      {(p.historico || []).length > 0 && (
                        <>
                          <div className="small fw-bold text-muted mt-3 mb-1"><i className="fas fa-clock-rotate-left me-1" aria-hidden="true"></i>Já enviados nesta competência</div>
                          {p.historico.map((h, i) => <div key={i} className="small text-muted border-bottom py-1"><b style={{ color: '#212529' }}>{h.enviado_em}</b> · {h.destinatarios}{h.enviado_por ? ` · por ${h.enviado_por}` : ''}</div>)}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #e9ecef' }}>
                  <span className="small text-muted me-auto">Nada é enviado sem você clicar em Enviar.</span>
                  <button type="button" className="btn btn-light border btn-sm" onClick={() => setEnvio(null)} disabled={envio.enviando}>Fechar</button>
                  <button type="button" className="btn btn-success btn-sm" onClick={enviar} disabled={!pronto || envio.enviando}
                    title={!pronto ? 'Escolha o operador e informe o e-mail do cliente' : undefined}>
                    <i className={`fas ${envio.enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`} aria-hidden="true"></i>{envio.enviando ? 'Enviando…' : 'Enviar ao cliente'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div></div>
  );
};

export default BpoRhCustoColaborador;
